import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { TrashIcon } from "../Common/icons";
import { outlinedPrimaryBtnSx } from "../Common/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { BindingDataDto, ParsedRowType } from "../../types";

type ParameterBindingSectionProps = {
  bindings: BindingDataDto[];
  parsed: ParsedRowType[];
  onBindingsChange: (bindings: BindingDataDto[]) => void;
  getIndexLabel: (id: string) => string;
  getBindingPreview: (row: BindingDataDto) => string;
};


const ParameterBindingSection: React.FC<ParameterBindingSectionProps> = ({
  bindings,
  parsed,
  onBindingsChange,
  getIndexLabel,
  getBindingPreview,
}) => {
  const [open, setOpen] = useState(false);

  const separatorOptions = [",", ".", "-", "/"] as const;
  const INDEX_EMPTY = "0";
  const INDEX_DEFAULT = "-1";

  const indexOptions: string[] = [INDEX_DEFAULT, INDEX_EMPTY, ...parsed.map((x) => x.id)];

  return (
    <Box sx={{ border: "1px solid #ddd", borderRadius: 2, bgcolor: "#fafafa", mb: 2 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          Parametre Bağlama
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            size="small"
            variant="outlined"
            sx={outlinedPrimaryBtnSx}
            onClick={() =>
              onBindingsChange([...bindings, { name: "", description: "", bindingParseData: [], separator: "," }])
            }
          >
            + Yeni Bağlama
          </Button>
          <IconButton size="small" onClick={() => setOpen((p) => !p)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box px={2} pb={2}>
          {bindings.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
              Parametre bağlamak için <strong>+ Yeni Bağlama</strong> butonuna basınız.
            </Typography>
          )}

          {bindings.map((row, rowIndex) => {
            const update = (patch: Partial<BindingDataDto>) =>
              onBindingsChange(bindings.map((b, i) => (i === rowIndex ? { ...b, ...patch } : b)));

            return (
              <Box key={rowIndex} sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 1.5, mb: 2, bgcolor: "#fff" }}>
                {/* Row fields */}
                <Grid container spacing={1} alignItems="center">
                  <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                    <TextField
                      size="small" fullWidth label="İsim"
                      value={row.name}
                      slotProps={{ htmlInput: { maxLength: 150 } }}
                      onChange={(e) => update({ name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                    <Autocomplete
                      size="small"
                      options={separatorOptions}
                      value={row.separator}
                      onChange={(_, val) => val && update({ separator: val })}
                      renderInput={(p) => <TextField {...p} label="Ayraç" />}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 4 }}>
                    <TextField
                      size="small" fullWidth label="Açıklama"
                      value={row.description}
                      slotProps={{ htmlInput: { maxLength: 150 } }}
                      onChange={(e) => update({ description: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 9, sm: 10, md: 3 }} display="flex" alignItems="center">
                    <Button
                      size="small" variant="outlined"
                      disabled={row.bindingParseData.length >= 3}
                      sx={{ ...outlinedPrimaryBtnSx, whiteSpace: "nowrap" }}
                      onClick={() =>
                        update({
                          bindingParseData: [
                            ...row.bindingParseData,
                            { itemId: "-1", order: row.bindingParseData.length + 1 },
                          ],
                        })
                      }
                    >
                      + Index Ekle
                    </Button>
                  </Grid>
                  <Grid size={{ xs: 3, sm: 2, md: 1 }} display="flex" justifyContent="flex-end" alignItems="center">
                    <IconButton size="small" onClick={() => onBindingsChange(bindings.filter((_, i) => i !== rowIndex))}>
                      <TrashIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>

                {/* Index items + preview */}
                {row.bindingParseData.length > 0 && (
                  <Grid container spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, md: 4 }} sx={{ borderLeft: "2px solid #e0e0e0", pl: 2 }}>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {row.bindingParseData.map((p, idx) => (
                          <Box key={p.order} display="flex" alignItems="center" gap={1}>
                            <Autocomplete<string, false, true, false>
                              size="small" disableClearable fullWidth
                              options={indexOptions}
                              value={p.itemId ?? INDEX_EMPTY}
                              getOptionLabel={(id) => getIndexLabel(id)}
                              isOptionEqualToValue={(o, v) => o === v}
                              onChange={(_, val) =>
                                update({
                                  bindingParseData: row.bindingParseData.map((x) =>
                                    x.order === p.order ? { ...x, itemId: val ?? null } : x
                                  ),
                                })
                              }
                              renderInput={(params) => <TextField {...params} label={`Index ${idx + 1}`} />}
                            />
                            <IconButton
                              size="small"
                              onClick={() =>
                                update({
                                  bindingParseData: row.bindingParseData
                                    .filter((x) => x.order !== p.order)
                                    .map((x, i) => ({ ...x, order: i + 1 })),
                                })
                              }
                            >
                              <TrashIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }} display="flex" alignItems="flex-start">
                      {getBindingPreview(row) && (
                        <Box sx={{ px: 2, py: 1, display: "inline-flex", alignItems: "center", borderRadius: 1, bgcolor: "#eef2ff", border: "1px dashed #6366f1", whiteSpace: "nowrap" }}>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#1e3a8a" }}>
                            {getBindingPreview(row)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ParameterBindingSection;
